// @flow

import React, { useEffect, useState } from 'react'
import { Link, useSearchParams } from 'react-router-dom'
import BackArrow from '../Shared/Icons/icon-backarrow-grey.svg'
import SubHeader from '../Shared/Display/SubHeader'
import styled from 'styled-components'
import InfoIcon from '../Shared/Icons/icon-info-grey.svg'
import PieChart from 'michelangelo/dist/WebComponents/Charts/PieChart'
import { useDispatch, useSelector } from 'react-redux'
import SmsUsersTable from '../Shared/Data/SmsUsersTable'
import {
  fetchSmsDetails,
  fetchSmsMembers,
  resetSmsDetails,
  resetSmsMembersSubscribed,
  resetSmsMembersUnsubscribed
} from '../Redux/Actions/Sms'
import { generateSMSUsersReport } from '../Redux/Actions/GenerateReport'

const GraphDiv = styled.div`
  ${(props) => {
  if (!props.isSmallScreen) {
    return `
      width: 50%;
      height: 100%;
    `
  } else {
    return `
      width: 100%;
      margin-bottom: 10px;
    `
  }
}}
  float: left;
`
const StyledDiv100 = styled.div`
  width: 100%;
  float: left;
  height: auto;
`
const LegendTable = styled.table`
  width: 280px;
  margin-bottom: 20px;
`
const LeftSpan = styled.span`
  float: left;
  margin: 0 12px;
`
const RightSpan = styled.span`
  float: right;
`
const LastUpdated = styled.div`
  padding-bottom: 20px;
  font-weight: 700;
`
const GraphsContainer = styled.div`
  padding-bottom: 20px;
  height: 320px;
`
const GraphContainer = styled.div`
  padding: 10px 0;
  height: 100%;
  background: #FFFFFF;
`
const ItemDetailLink = styled.h6`
  color: #3FA8F0;
  font-size: 16px;
  font-weight: 700;
`

const generateTableOptions = (tooltips) => {
  return {
    maintainAspectRatio: false,
    responsive: false,
    // callback to build a html legend as needed
    legendCallback: function (chart) {
      const text = []
      text.push('<ul style="width: 280px; float: left;">')
      text.push('<table style="width: 280px;">')
      text.push('<tbody>')
      const dataset = chart.data.datasets[0]
      for (let i = 0; i < dataset.data.length; i++) {
        text.push('<tr>')
        text.push('<td>')
        text.push('<li>')
        text.push(`
        <span style="background-color: ${dataset.backgroundColor[i]};
                     margin: 12px;
                     height: 12px;
                     width: 12px;
                     border-radius: 50%;
                     display: inline-block;"></span>
      `)
        text.push('</td>')

        if (chart.data.labels[i]) {
          text.push('<td style="padding-top: 7px;">')
          text.push('<span style="margin: 12px;">')
          text.push(chart.data.labels[i])
          text.push('</span>')
          if (chart.data.labels.length > 2) {
            text.push('<div class="tooltip">')
            text.push('<img src=' + InfoIcon + ' alt=' + InfoIcon + ' />')
            text.push('</span>')
            text.push('<span class="tooltipText">' + tooltips[i] + '</span>')
            text.push('</div>')
          }
          text.push('</td>')
        }
        text.push('<td>')
        text.push('<span style="margin-left: 30px; float: right;">' + dataset.data[i] + '</span>')
        text.push('</td>')
        text.push('</li>')
        text.push('</td>')
        text.push('</tr>')
      }
      text.push('</tbody>')
      text.push('</table>')
      text.push('</ul>')
      return text.join('')
    }
  }
}

const deliveryLabels = ['Delivered', 'Undelivered', 'Failed']
const deliveryBgColors = ['#0ABD63', '#ECB33D', '#F07B7B']
const deliveryTooltips = [
  'Twilio has received confirmation of message delivery from the upstream carrier, and, where available, the destination handset.',
  'Twilio has received a delivery receipt indicating that the message was not delivered. This can happen for a number of reasons ' +
  'including carrier content filtering, availability of the destination handset, etc.',
  'The message could not be sent. This can happen for various reasons including queue overflows, account suspensions and media ' +
  'errors (in the case of MMS). Twilio does not charge you for failed messages.'
]
const deliveryOptions = generateTableOptions(deliveryTooltips)

const subLabels = ['Subscribed', 'Unsubscribed', 'Pending']
const subBgColors = ['#3FA8F0', '#BDBDBD', '#75D5E2']
const subTooltips = [
  'Total Number of users in the targeted groups that are confirmed opted-in',
  'Total Number of users in the targeted groups that are confirmed opted-out',
  'Total Number of users in the targeted groups that are pending invite and pending response'
]
const subOptions = generateTableOptions(subTooltips)

const RESULTS_PER_PAGE = 5

const startCase = (str) => {
  return str.toLowerCase()
    .replace(/\b\w/g, (match) => {
      return match.toUpperCase()
    })
}

function SmsDetails () {
  const dispatch = useDispatch()
  const [searchParams] = useSearchParams()
  const alertId = searchParams.get('id')

  const [isSmallScreen, setIsSmallScreen] = useState(!(window.innerWidth >= 1472))

  const data = useSelector(state => state.sms.smsDetails)
  const smsMembersSubscribed = useSelector(state => state.sms.smsMembersSubscribed)
  const smsMembersUnsubscribed = useSelector(state => state.sms.smsMembersUnsubscribed)
  const status = useSelector(state => state.generateReport.status)

  const [updatedDate, setUpdatedDate] = useState('')
  const [dataReceived, setDataReceived] = useState(false)
  const [deliveryCounts, setDeliveryCounts] = useState([])
  const [memberCounts, setMemberCounts] = useState([])
  const [totalSubbedUsers, setTotalSubbedUsers] = useState(0)
  const [totalUsers, setTotalUsers] = useState(0)

  const [subscribedMembers, setSubscribedMembers] = useState({})
  const [unsubbedPendingMembers, setUnsubbedPendingMembers] = useState({})
  const [subscribedPage, setSubscribedPage] = useState(1)
  const [unSubbedPendingPage, setUnSubbedPendingPage] = useState(1)

  const handleResize = () => {
    setIsSmallScreen(!(window.innerWidth >= 1472))
  }
  useEffect(() => {
    window.addEventListener('resize', handleResize)
    return () => {
      window.removeEventListener('resize', handleResize)
    }
  }, [])

  useEffect(() => {
    dispatch(fetchSmsDetails(alertId))
    dispatch(fetchSmsMembers(alertId, true, {}))
    dispatch(fetchSmsMembers(alertId, false, {}))
  }, [])

  useEffect(() => {
    return () => {
      dispatch(resetSmsDetails())
      dispatch(resetSmsMembersSubscribed())
      dispatch(resetSmsMembersUnsubscribed())
    }
  }, [])

  useEffect(() => {
    const options = { limit: RESULTS_PER_PAGE, offset: RESULTS_PER_PAGE * (subscribedPage - 1) }
    dispatch(fetchSmsMembers(alertId, true, options))
  }, [subscribedPage])

  useEffect(() => {
    const options = { limit: RESULTS_PER_PAGE, offset: RESULTS_PER_PAGE * (unSubbedPendingPage - 1) }
    dispatch(fetchSmsMembers(alertId, false, options))
  }, [unSubbedPendingPage])

  useEffect(() => {
    if (data.title !== 'Loading' && !dataReceived) {
      const deliveryData = data.deliveryCounts
      setDeliveryCounts([
        deliveryData.delivered,
        deliveryData.undelivered,
        deliveryData.failed
      ])
      const memberData = data.memberCounts
      setMemberCounts([
        memberData.subscribed,
        memberData.unsubscribed,
        memberData.pending
      ])
      setTotalSubbedUsers(memberData.subscribed)
      setTotalUsers(memberData.subscribed + memberData.unsubscribed + memberData.pending)
      setDataReceived(true)
      setUpdatedDate((new Date()).toString())
    }
  }, [data])

  useEffect(() => {
    if (smsMembersSubscribed.members) {
      smsMembersSubscribed.members.forEach((element) => {
        element.status = startCase(element.status)
      })
    }
    setSubscribedMembers(smsMembersSubscribed)
  }, [smsMembersSubscribed])

  useEffect(() => {
    if (smsMembersUnsubscribed.members) {
      smsMembersUnsubscribed.members.forEach((element) => {
        element.status = startCase(element.status)
      })
    }
    setUnsubbedPendingMembers(smsMembersUnsubscribed)
  }, [smsMembersUnsubscribed])
  const generateReport = (subscribed = true) => {
    dispatch(generateSMSUsersReport(alertId, subscribed, RESULTS_PER_PAGE, 0))
  }
  return (
    <div id='SmsDetailsPage'>
      <div className='toolbar'>
        <Link to={'/notifications'}>
          <img src={BackArrow} className='interactive' alt='back' />
        </Link>
        <div className='subTitle'>
          <h2>{data.title}</h2>
          <SubHeader type='NotificationDetails' value='SMS Details' />
          <Link to={{ pathname: '/notifications' }}>
            <ItemDetailLink>Go to Notification Overview Page</ItemDetailLink>
          </Link>
        </div>
      </div>
      <StyledDiv100>
        <LastUpdated>Last Updated {updatedDate}</LastUpdated>
        <GraphsContainer>
          <GraphDiv isSmallScreen={isSmallScreen}>
            <GraphContainer className={isSmallScreen ? '' : 'marginRight10'}>
              <div className='chartTitle'>
                <h3>Delivery Statistics</h3>
              </div>
              <LegendTable>
                <tbody>
                <tr>
                  <td>
                    <StyledDiv100>
                      <LeftSpan>Total Subscribed Users</LeftSpan>
                      <div className='tooltip'><span><img src={InfoIcon} alt={InfoIcon} /></span>
                        <span className='tooltipText'>Total Number of users that are confirmed opted-in.</span>
                      </div>
                    </StyledDiv100>
                  </td>
                  <td><RightSpan>{totalSubbedUsers}</RightSpan></td>
                </tr>
                </tbody>
              </LegendTable>
              {deliveryCounts.length > 0 && (
                <PieChart chartId='chart1' data={deliveryCounts} labels={deliveryLabels} colors={deliveryBgColors} options={deliveryOptions} height={200} />
              )}
            </GraphContainer>
          </GraphDiv>
          <GraphDiv isSmallScreen={isSmallScreen}>
            <GraphContainer className={isSmallScreen ? '' : 'marginLeft10'}>
              <div className='chartTitle'>
                <h3>Subscription Statistics</h3>
              </div>
              <LegendTable>
                <tbody>
                <tr>
                  <td>
                    <StyledDiv100>
                      <LeftSpan>Total Users</LeftSpan>
                      <div className='tooltip'><span><img src={InfoIcon} alt={InfoIcon} /></span>
                        <span className='tooltipText'>
                          Total Number of users in the targeted groups that are opted-in, opted-out, pending (invite and response).
                        </span>
                      </div>
                    </StyledDiv100>
                  </td>
                  <td><RightSpan>{totalUsers}</RightSpan></td>
                </tr>
                </tbody>
              </LegendTable>
              {memberCounts.length > 0 && (
                <PieChart chartId='chart2' data={memberCounts} labels={subLabels} colors={subBgColors} options={subOptions} height={200} />
              )}
            </GraphContainer>
          </GraphDiv>
        </GraphsContainer>
      </StyledDiv100>
      <StyledDiv100>
        <SmsUsersTable
          title='Subscribed Users'
          data={subscribedMembers.members || []}
          fields={[
            { ref: 'regCode', title: 'Regcode' },
            { ref: 'name', title: 'User Name' },
            { ref: 'phoneNumber', title: 'Mobile Phone Number' },
            { ref: 'email', title: 'Email' }
          ]}
          generateReport={() => generateReport()}
          reportStatus={status}
          changeOffset={(i) => { setSubscribedPage(i) }}
          page={subscribedPage}
          totalUsers={Math.ceil(subscribedMembers.count / RESULTS_PER_PAGE)}
          tooltipText='Registered and unregistered subscribed users in the groups associated with the SMS Alert at the time it was sent.'
        />
      </StyledDiv100>
      <StyledDiv100>
        <SmsUsersTable
          title='Unsubscribed and Pending Users'
          data={unsubbedPendingMembers.members || []}
          fields={[
            { ref: 'regCode', title: 'Regcode' },
            { ref: 'status', title: 'Status' },
            { ref: 'name', title: 'User Name' },
            { ref: 'phoneNumber', title: 'Mobile Phone Number' },
            { ref: 'email', title: 'Email' }
          ]}
          generateReport={() => generateReport(false)}
          reportStatus={status}
          changeOffset={(i) => { setUnSubbedPendingPage(i) } }
          page={unSubbedPendingPage}
          totalUsers={Math.ceil(unsubbedPendingMembers.count / RESULTS_PER_PAGE)}
          tooltipText='Registered and unregistered unsubscribed and pending users in the groups associated with the SMS Alert at the time it was sent.'
        />
      </StyledDiv100>
    </div>
  )
}

export default SmsDetails
